import {BucketResult} from '../models/schema/aggregation/bucket-result';
import {OperatorType, SeasonalOperatorParam, TimeIntervalUnit} from '../enums/ngsi-query.enum';
import {NGSIResult} from '../models/ngsi/ngsi-result';
import {KPI} from '../models/kpi.model';
import {ChartSettings} from '../models/visualization/chart-settings.model';
import {AggregationResult} from '../models/schema/aggregation/aggregation-result';
import {AxisSettings} from '../models/visualization/axis-settings.model';
import {DimensionSeriesData} from '../models/report/dimension-series-data.model';
import {DataFormatUtil} from './data-format-util';
import {ChartDataFormat} from '../enums/chart-data-format';
import {Injectable} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {QueryOperator} from '../models/query/query-operator.model';
import {QueryOperatorParamValue} from '../models/query/query-operator-param-value.model';
import {UrukAggregationQuery} from '../models/ngsi/uruk-aggregation-query.model';
import {FormUtil} from './form-util';
import {NGSIResultUtil} from './ngsi-result-util';
import {EntityResult} from '../models/schema/entity-result';
import {UrukCrossAggQuery} from '../models/ngsi/uruk-cross-agg-query.model';
import {ColorUtil} from "./color-util";
import * as echarts from 'echarts';

/**
 * Utility class to extract data from {@link NGSIResult}s based on associated {@link KPI} and {@link ChartSettings}
 */
@Injectable({
  providedIn: 'root'
})
export class DataExtractionUtil {
  constructor(private translateService: TranslateService) {
  }

  /**
   * Extracts data from {@link NGSIResult}s based on associated {@link KPI} and {@link ChartSettings}
   * @param kpi
   * @param chartSettings
   * @param results
   */
  public extractData(kpi: KPI, chartSettings: ChartSettings, results: NGSIResult[]): [ DimensionSeriesData[], any[]? ] {
    const extractedResults: DimensionSeriesData[] = [];
    // populate settings
    // dimensions
    let colors:any[];
    for (let i = 0; i < chartSettings.dimensionSettings.length; i++) {
      const dimensionData: DimensionSeriesData = new DimensionSeriesData();
      dimensionData.dimension = kpi.dimensions[chartSettings.dimensionSettings[i].index];
      dimensionData.chartAxisSetting = chartSettings.dimensionSettings[i];
      extractedResults.push(dimensionData);
    }
    // series
    for (let i = 0; i < chartSettings.seriesSettings.length; i++) {
      const seriesData: DimensionSeriesData = new DimensionSeriesData();
      seriesData.series = kpi.series[chartSettings.seriesSettings[i].index];
      seriesData.chartAxisSetting = chartSettings.seriesSettings[i];
      extractedResults.push(seriesData);
    }

    if (results.length > 0) {
      // parse results based on the type of the result
      const firstResult: NGSIResult = results[0];

      // aggregation results
      if (firstResult.result instanceof AggregationResult) {
        for (let i = 0; i < chartSettings.seriesSettings.length; i++) {
          const seriesData: DimensionSeriesData = extractedResults[i];
          seriesData.data.push(this.extractAggregationResult(results[0].getAggregationResult(), chartSettings.seriesSettings[i]));
          extractedResults[i] = seriesData;
        }

        // process root-level buckets recursively
      } else if (firstResult.result instanceof BucketResult) {
        results.forEach(result => {
          this.extractBucketResult(kpi, 0, [], chartSettings, result.getBucketResult(), extractedResults);
        });

        // entity results
      } else {
        results.forEach(result => {
          this.extractEntityResult(chartSettings, result, extractedResults);
        });
      }
    }
    // check user defined colors for a chart only if
    //    there is data in the panel's chartSettings.settings.colorMap,
    //    and the chart has dimension data (there is data in chartSettings.dimensionSettings)
    if ( (chartSettings.settings?.colorMap) && (chartSettings.settings?.colorMap?.length >0) ){
      extractedResults.forEach(result => {
        if ( result.dimension && result.data?.length) {
          colors = this.extractColors(result, chartSettings);
        }
      });
    }
    // TODO: handle multidimensional/multiseries charts
    // translate dimension labels
    extractedResults.forEach(result => {
      if (result.dimension && result.data?.length) {
        this.translateService.get(result.data).subscribe(translations => {
          //result.data = Object.values(translations);
          // set the translated label for each data
          result.data = result.data.map(data => translations[data])
        });

      //  result.data = result.data.map(data => this.translateService.instant(data));
      }
    });

    return [extractedResults, colors];
  }

  /**
   * Extracts user defined colors from chartSettings.settings.colorMap of the related panel,
   * Matches the colors with kpi values if there is a kpiValue - color pair in colorMap
   * Called only if a panel's chartSettings has settings.colorMap array,
   * colorMap array has user defined colors for the chart, and may have kpiValue - color pairs
   * colorMap supports several formats such as
   * colorMap = [
   *       {color: "orange"},
   *       {color: "hsl(250,50%,70%)"},
   *       "green",
   *       "#556791",
   *       {"kpiValue": "lowbattery", "color": "#e3bb2c"},
   *       {"color": "red", "kpiValue": "error"}
   *     ];
   * @param result is the mapped array of chartSettings.dimensionSettings array in extractData() method
   * @param chartSettings is the chartSettings for the related Chart Component (i.e. pie-chart, bar-chart, line-chart, gauge) of a panel
   * @private
   */
  private extractColors(result: DimensionSeriesData, chartSettings: ChartSettings): any[] {
    let colors=[];
    // the color values in ColorMap are checked and only the colors with correct color format are filtered
    let colorMap = chartSettings.settings.colorMap.filter(item => {
      const color = item.color ? item.color : item;
      const parsedColor = echarts.color.parse(color);
      return parsedColor != null;
    });
    let colorMapLen: number = colorMap.length;

    // if # of data is more than # of colors in a chart, repeatColors defines how the missing colors will be generated
    // if repeatColors is true, the colors in colorMap will be repeatedly used for missing colors
    // default value for repeatColors is false
    let repeatColors:boolean = chartSettings.settings.repeatColors !== undefined ? chartSettings.settings.repeatColors && colorMapLen > 1: false;
    let tempColor = colorMap.map(item => item.color ? item.color : item);
    //variable for generating missing colors
    let newColorCount: number = 0;
    // checks if colorMap has some kpiValue - color pair
    if (colorMap.some(element => element.kpiValue )) {
      let colorsCompleted: boolean = true;
      let colorPaletteIndex: number = 0;
      // first the paired colors are assigned for the data that match with kpiValues in colorMap
      for ( let i = 0;colorMapLen != 0 && i<result.data.length;i++) {
        let index = colorMap.findIndex( item => item.kpiValue !== undefined && item.kpiValue == result.data[i] );
        if (index !=-1) {
          colors.push(tempColor[index]);
          tempColor[index]=null;
        }
        else {
          colors.push(null);
          colorsCompleted = false;
        }
      }
      // after colors are assigned to data with matching kpiValues in colorMap, checks whether there are any remaining data without color
      if (colorsCompleted == false) {
        let index: number = 0;
        for ( let i = 0;i<result.data.length;i++) {
          if (colors[i] == null) {
            let colorPushed: boolean = false;
            while (index < colorMapLen) {
              if (tempColor[index] != null) {
                colors[i] = tempColor[index++];
                colorPushed = true;
                break;
              }
              index++;
            }
            if (!colorPushed) {
              // all colors in colorMap are used, and there are still missing colors for some data
              // if repeatColors, use only the colors in colorMap for missing colors
              if (repeatColors) {
                colors[i] = colorMap[newColorCount % colorMapLen].color ? colorMap[newColorCount % colorMapLen].color : colorMap[newColorCount % colorMapLen];
                newColorCount++;
              }
              // if repeatColors is false first use the colors in colorPalette
              else if (colorPaletteIndex < ColorUtil.colorPalette.length)
                colors[i] = ColorUtil.colorPalette[colorPaletteIndex++];
              else {
                // color generation algorithm, if # of colors in colorPalette is not sufficient for missing colors
                let totColorLen = result.data.length-colorMapLen-ColorUtil.colorPalette.length;
                let hue: number = Math.round( 360*( ((newColorCount++)%totColorLen) / totColorLen) );
                colors[i] = `hsl(${hue}, 70%, ${30+20*(i%3)}%)`;
              }
            }
          }
        }
      }
    }
    // if colorMap has no kpiValue - color pair
    else {
      for ( let i = 0;i<result.data.length;i++) {
        // first use colors in colorMap
        if (i<colorMapLen)
          colors.push(tempColor[i]);
        // if repeatColors, use only the colors in colorMap for missing colors
        else if (repeatColors)
          colors.push(tempColor[i%colorMapLen]);
        // if repeatColors is false first use the colors in colorPalette
        else if (i<colorMapLen+ColorUtil.colorPalette.length)
          colors.push(ColorUtil.colorPalette[i-colorMapLen]);
        // color generation algorithm, if # of colors in colorPalette is not sufficient for missing colors
        else {
          let totColorLen = result.data.length-colorMapLen-ColorUtil.colorPalette.length;
          let hue: number = Math.round( 360*( ((newColorCount++)%totColorLen) / totColorLen) );
          colors.push(`hsl(${hue}, 70%, ${30+20*(i%3)}%)`);
        }
      }
    }
    return colors;
  }

  /**
   * Extracts data for a {@link BucketResult} recursively. It populates the columnsData as the buckets are processed
   * @param kpi KPI for which the results are obtained
   * @param dimensionIndex Index of the processed dimension
   * @param extractedRowValues Values extracted so for for the processed dimensions
   * @param chartSettings All chart settings that are referred to access dimension or series settings
   * @param bucketResult Bucket result
   * @param columnsData Data structure containing the eventual data to be extracted
   * @private
   */
  private extractBucketResult(kpi: KPI,
                              dimensionIndex: number,
                              extractedRowValues: string[],
                              chartSettings: ChartSettings,
                              bucketResult: BucketResult,
                              columnsData: DimensionSeriesData[]): void {

    const chartDimensionSettings: AxisSettings = columnsData[dimensionIndex].chartAxisSetting;
    // if we reach the last dimension, extract dimension label from key and values for each series from the actual value
    if (this.isLastDimension(kpi, dimensionIndex)) {
      // extract value for the dimension
      let bucketAggregation: QueryOperator;

      if (kpi.query instanceof UrukAggregationQuery) {
        bucketAggregation = (kpi.query as UrukAggregationQuery).bucketAgg[dimensionIndex];
      } else if (kpi.query instanceof UrukCrossAggQuery) {
        // TODO: below logic handles left and inner joins ([0]), consider handling other join types (e.g. right) as well
        bucketAggregation = (kpi.query as UrukCrossAggQuery).subQueries[0].bucketAgg[dimensionIndex];
      }

      const dataType: ChartDataFormat = this.getDataType(bucketAggregation, columnsData[dimensionIndex]);
      const dimensionValue: any = DataFormatUtil.formatValue(
        this.extractKeyValue(bucketAggregation, chartDimensionSettings, bucketResult), dataType, chartDimensionSettings.precision, chartDimensionSettings.thousandSeparator
      );

      // extract values for series
      const seriesValues: any[] = chartSettings.seriesSettings.map(seriesSettings => {
        return this.extractAggregationResult(bucketResult.aggregationResult, seriesSettings);
      });

      const rowValues: any[] = extractedRowValues.concat([dimensionValue]).concat(seriesValues);

      // put each value into corresponding dimension or series data array
      // if there is at least a null or empty value, skip the row completely
      if (!rowValues.some(value => FormUtil.isEmptyValue(value))) {
        for (let i: number = 0; i < columnsData.length; i++) {
          columnsData[i].data.push(rowValues[i]);
        }
      }

    } else {
      const bucketAggregation: QueryOperator = (kpi.query as UrukAggregationQuery).bucketAgg[dimensionIndex];
      const dataType: ChartDataFormat = kpi.dimensions[dimensionIndex].keys[chartDimensionSettings.path].dataType;
      const dimensionValue: any = DataFormatUtil.formatValue(
        this.extractKeyValue(bucketAggregation, chartDimensionSettings, bucketResult), dataType, chartDimensionSettings.precision, chartDimensionSettings.thousandSeparator
      );

      // fetch the dimension settings for the next dimension
      // chartDimensionSettings = chartSettings.dimensionSettings.find(settings => settings.index === (dimensionIndex + 1));
      bucketResult.bucketResults.forEach(subResult => {
        this.extractBucketResult(kpi, dimensionIndex + 1, extractedRowValues.concat([dimensionValue]), chartSettings, subResult, columnsData);
      });
    }
  }

  /**
   * Returns the data format for the given aggregation operation.
   * @param aggregationOperation the query operator for aggration
   * @param columnsData dimension series data
   * @return the corresponding chart data format.
   * */
  private getDataType(aggregationOperation: QueryOperator, columnsData: DimensionSeriesData): ChartDataFormat {
    const operator: string = aggregationOperation.op;
    switch (operator) {
      case OperatorType.TIME_INTERVAL: {
        if (aggregationOperation.params[0] instanceof QueryOperatorParamValue) {
          const paramValue: QueryOperatorParamValue = aggregationOperation.params[0] as QueryOperatorParamValue;
          const timeIntervalUnit = paramValue.value[paramValue.value.length - 1];
          switch (timeIntervalUnit) {
            case TimeIntervalUnit.DAY:
              return ChartDataFormat.DATE;
            case TimeIntervalUnit.HOUR:
              return ChartDataFormat.HOUR;
            case TimeIntervalUnit.MONTH:
              return ChartDataFormat.MONTH;
            case TimeIntervalUnit.YEAR:
              return ChartDataFormat.YEAR;
          }
        }
      }
    }
    return columnsData.getDataType();
  }

  private extractEntityResult(chartSettings: ChartSettings, result: NGSIResult, columns: DimensionSeriesData[]): void {
    const values: any[] = [];
    for (let i = 0; i < columns.length; i++) {
      const entityResult: EntityResult = result.getEntityResult();
      let value: any = NGSIResultUtil.extractEntityAttributeValue(entityResult.entity, columns[i].chartAxisSetting.path, entityResult.queryOptions);

      if (i < chartSettings.dimensionSettings.length) {
        const dataType: ChartDataFormat = columns[i].getDataType();
        const precision: number = columns[i].chartAxisSetting.precision;
        const thousandSeparator: boolean = columns[i].chartAxisSetting.thousandSeparator;
        value = DataFormatUtil.formatValue(value, dataType, precision, thousandSeparator);
      }

      if (value === null) {
        return null;
      }
      values.push(value);
    }

    for (let i = 0; i < values.length; i++) {
      columns[i].data.push(values[i]);
    }
  }

  private extractAggregationResult(result: AggregationResult, seriesSetting: AxisSettings): any {
    return result.result[seriesSetting.path];
  }

  private isLastDimension(kpi: KPI, dimensionIndex: number): boolean {
    if (kpi.dimensions.length === dimensionIndex + 1) {
      return true;
    } else {
      return false;
    }
  }

  private extractKeyValue(aggregationOperation: QueryOperator, chartDimensionSettings: AxisSettings, bucketResult: BucketResult): any {
    const operator: string = aggregationOperation.op;
    switch (operator) {
      case OperatorType.SEASONAL: {
        return this.extractValueForSeasonalOperator(aggregationOperation.params[0] as QueryOperatorParamValue, bucketResult.key);
      }
      case OperatorType.GROUP_BY: {
        // TODO comment on executing ngsi path on key field
        return this.extractValueForGroupByOperator(chartDimensionSettings, bucketResult.key);
      }
      case OperatorType.TIME_INTERVAL: {
        return this.extractValueForTimeIntervalOperator(bucketResult.key);
      }
      case OperatorType.MAX_N:{
        return this.extractValueForMaxNOperator(chartDimensionSettings, bucketResult.key);
      }
    }
    console.error('Unhandled aggregation operator', operator);
    return '';
  }

  private extractValueForMaxNOperator(chartDimensionSetting: AxisSettings, key: any): any {
    //console.log(chartDimensionSetting.path+" "+key.linecode);

    return key[chartDimensionSetting.path];
  }

  private extractValueForGroupByOperator(chartDimensionSetting: AxisSettings, key: any): any {
    return key[chartDimensionSetting.path];
  }

  private extractValueForTimeIntervalOperator(key: any): any {
    return key.ts;
  }

  private extractValueForSeasonalOperator(param: QueryOperatorParamValue, key: any): any {
    const season: any = key.season;
    if (param.value === SeasonalOperatorParam.DAY_OF_WEEK) {
      // TODO uncomment the following line when the charts adopt the data extraction util
      return this.getDayOfWeekSeasonalValue(season);
      // return season;
    }
    console.error('Unhandled seasonal operator parameter', param);
    return '';
  }

  private getDayOfWeekSeasonalValue(result: number): string {
    switch (result) {
      case 0.0:
        return this.translateService.instant('Pazar');
      case 1.0:
        return this.translateService.instant('Pazartesi');
      case 2.0:
        return this.translateService.instant('Salı');
      case 3.0:
        return this.translateService.instant('Çarşamba');
      case 4.0:
        return this.translateService.instant('Perşembe');
      case 5.0:
        return this.translateService.instant('Cuma');
      case 6.0:
        return this.translateService.instant('Cumartesi');
    }
    console.warn('Invalid day of week seasonal value:', result);
    return '';
  }
}
